/**
 * I pezzi comuni dei widget. Ognuno sa se sta su un widget colorato (testo
 * bianco) o chiaro (testo scuro) e si colora da se' (vedi temaContesto.ts).
 * Cornice, titolo e controlli li mette CorniceWidget.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowDownRight, ArrowUpRight, RefreshCw, type LucideIcon } from 'lucide-react';
import { cn } from '../../../lib/cn';
import type { StatoFonte } from '../../../lib/dashboard/cacheFonti';
import type { Taglia } from '../../../lib/dashboard/catalogo';
import { percentualeConSegno } from '../../../lib/dashboard/formato';
import { useTemaScuro } from '../temaContesto';

export function Scheletro({ righe = 2 }: { righe?: number }) {
  const scuro = useTemaScuro();
  const tinta = scuro ? 'bg-white/25' : 'bg-black/[0.06]';
  return (
    <div className="flex h-full animate-pulse flex-col justify-end gap-2" aria-label="Caricamento">
      <div className={cn('h-8 w-1/2 rounded-xl', tinta)} />
      {Array.from({ length: righe }, (_, i) => (
        <div key={i} className={cn('h-2.5 rounded-full', tinta)} style={{ width: `${75 - i * 18}%` }} />
      ))}
    </div>
  );
}

export function StatoErrore({ onRiprova }: { onRiprova: () => void }) {
  const scuro = useTemaScuro();
  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 text-center">
      <p className="flex items-center gap-1.5 text-xs font-medium opacity-80">
        <AlertTriangle className="h-3.5 w-3.5" /> Non riesco a leggere i dati
      </p>
      <button
        type="button"
        onClick={onRiprova}
        className={cn('flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition', scuro ? 'bg-white/20 hover:bg-white/30' : 'bg-neutral-900 text-white hover:bg-neutral-700')}
      >
        <RefreshCw className="h-3 w-3" /> Riprova
      </button>
    </div>
  );
}

/**
 * Quando non c'e' niente da mostrare: un'icona grande e morbida, una frase
 * chiara e, se serve, cosa fare. Mai un riquadro vuoto.
 */
export function StatoVuoto({ icona: Icona, testo, sotto, azione, compatto = false }: {
  icona: LucideIcon;
  testo: string;
  sotto?: string;
  azione?: { etichetta: string; percorso: string };
  compatto?: boolean;
}) {
  const scuro = useTemaScuro();
  return (
    <div className={cn('flex h-full items-center gap-3', compatto ? 'flex-row' : 'flex-col justify-center text-center')}>
      <span className={cn('flex flex-shrink-0 items-center justify-center rounded-2xl', compatto ? 'h-10 w-10' : 'h-12 w-12', scuro ? 'bg-white/15' : 'bg-black/[0.04]')}>
        <Icona className={cn(compatto ? 'h-5 w-5' : 'h-6 w-6', 'opacity-80')} />
      </span>
      <div className="min-w-0">
        <p className="font-display text-sm font-semibold leading-tight">{testo}</p>
        {sotto && <p className="mt-0.5 text-xs opacity-70">{sotto}</p>}
        {azione && (
          <Link
            to={azione.percorso}
            className={cn('mt-2 inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs font-semibold transition', scuro ? 'bg-white text-neutral-900 hover:bg-white/90' : 'bg-neutral-900 text-white hover:bg-neutral-700')}
          >
            {azione.etichetta} <ArrowUpRight className="h-3 w-3" />
          </Link>
        )}
      </div>
    </div>
  );
}

/**
 * Mostra il contenuto quando i dati ci sono, altrimenti lo stato giusto. Se
 * un aggiornamento fallisce ma ci sono i dati di prima, restano quelli con
 * un avviso: meglio un numero di cinque minuti fa che nessun numero.
 */
export function Fonte<T>({ stato, onRiprova, children }: {
  stato: StatoFonte<T>;
  onRiprova: () => void;
  children: (dati: T) => ReactNode;
}) {
  if (stato.stato === 'errore' && stato.dati === undefined) return <StatoErrore onRiprova={onRiprova} />;
  if (stato.stato === 'attesa' || (stato.stato === 'caricamento' && stato.dati === undefined)) return <Scheletro />;
  const dati = stato.dati as T;
  return (
    <div className="relative flex h-full min-h-0 flex-col">
      {children(dati)}
      {stato.stato === 'errore' && (
        <button type="button" onClick={onRiprova} title={stato.errore} className="absolute right-0 top-0 flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800">
          <AlertTriangle className="h-3 w-3" /> non aggiornato
        </button>
      )}
    </div>
  );
}

const DIMENSIONI_NUMERO: Record<Taglia, string> = {
  S: 'text-[clamp(1.5rem,2.3vw,2.1rem)]',
  M: 'text-[clamp(1.9rem,3vw,2.6rem)]',
  L: 'text-5xl',
  XL: 'text-5xl',
};

/** Il numero protagonista del widget. */
export function NumeroGrande({ valore, taglia = 'S', className }: { valore: ReactNode; taglia?: Taglia; className?: string }) {
  return (
    // L'interlinea va dopo la dimensione: chi unisce le classi toglie
    // un'interlinea scritta prima di una dimensione del testo.
    <div className={cn('truncate font-display font-bold tracking-tight tabular-nums', DIMENSIONI_NUMERO[taglia], 'leading-none', className)}>
      {valore}
    </div>
  );
}

export function Etichetta({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={cn('truncate text-xs font-medium opacity-75', className)}>{children}</p>;
}

/** Una pillola: su un widget colorato e' vetro bianco, su uno chiaro prende il colore passato. */
export function Pillola({ children, colore, className, title }: { children: ReactNode; colore?: string; className?: string; title?: string }) {
  const scuro = useTemaScuro();
  return (
    <span title={title} className={cn('inline-flex max-w-full items-center gap-1 truncate rounded-full px-2 py-0.5 text-[11px] font-semibold', scuro ? 'bg-white/20 text-white' : (colore ?? 'bg-black/[0.05] text-neutral-700'), className)}>
      {children}
    </span>
  );
}

/** "+12% sull'anno prima", come pillola verde o rossa. */
export function Variazione({ percentuale, rispetto }: { percentuale: number | null; rispetto: string }) {
  if (percentuale === null) return <Etichetta>nessun confronto {rispetto}</Etichetta>;
  const su = percentuale >= 0;
  const Icona = su ? ArrowUpRight : ArrowDownRight;
  return (
    <span className="flex min-w-0 items-center gap-1.5" title={`${percentualeConSegno(percentuale)} ${rispetto}`}>
      <Pillola colore={su ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'} className="flex-shrink-0">
        <Icona className="h-3 w-3" /> {percentualeConSegno(percentuale)}
      </Pillola>
      <span className="truncate text-[11px] opacity-70">{rispetto}</span>
    </span>
  );
}

export function Barra({ quota, colore }: { quota: number; colore: string }) {
  const scuro = useTemaScuro();
  const larghezza = Math.max(0, Math.min(1, quota)) * 100;
  return (
    <div className={cn('h-2 flex-1 overflow-hidden rounded-full', scuro ? 'bg-white/20' : 'bg-black/[0.06]')}>
      <div className={cn('h-full rounded-full', colore)} style={{ width: `${larghezza > 0 ? Math.max(larghezza, 4) : 0}%` }} />
    </div>
  );
}

/** Le iniziali in un tondino colorato: danno un volto a clienti e lead. */
export function Iniziali({ nome, colore }: { nome: string; colore: string }) {
  const lettere = nome.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?';
  return (
    <span className={cn('flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full text-[10px] font-bold', colore)}>
      {lettere}
    </span>
  );
}

/**
 * Un minigrafico ad area, disegnato in fondo al widget: l'andamento si
 * intuisce senza leggere niente. Usa il colore del testo (bianco o scuro).
 */
export function Andamento({ valori, className }: { valori: readonly number[]; className?: string }) {
  if (valori.length < 2 || valori.every((v) => v === 0)) return null;
  const massimo = Math.max(...valori, 1);
  const punti = valori.map((v, i) => [(i / (valori.length - 1)) * 100, 36 - (Math.max(0, v) / massimo) * 32] as const);
  const linea = punti.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(2)},${y.toFixed(2)}`).join(' ');
  return (
    <svg viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden className={cn('pointer-events-none', className)}>
      <path d={`${linea} L100,40 L0,40 Z`} className="fill-current opacity-[0.18]" />
      <path d={linea} className="stroke-current opacity-60" fill="none" strokeWidth="1.6" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
    </svg>
  );
}

/** Un anello di avanzamento con la percentuale al centro. */
export function Anello({ quota, testo, dimensione = 64 }: { quota: number | null; testo: string; dimensione?: number }) {
  const r = 26;
  const circonferenza = 2 * Math.PI * r;
  const pieno = Math.max(0, Math.min(1, quota ?? 0)) * circonferenza;
  return (
    <div className="relative flex-shrink-0" style={{ width: dimensione, height: dimensione }}>
      <svg viewBox="0 0 64 64" className="h-full w-full -rotate-90" aria-hidden>
        <circle cx="32" cy="32" r={r} fill="none" strokeWidth="7" className="stroke-current opacity-20" />
        <circle cx="32" cy="32" r={r} fill="none" strokeWidth="7" strokeLinecap="round" className="stroke-current" strokeDasharray={`${pieno} ${circonferenza}`} />
      </svg>
      <span className="absolute inset-0 flex items-center justify-center font-display text-sm font-bold">{testo}</span>
    </div>
  );
}

/** Un grafico a ciambella: ogni fetta ha la sua classe di colore del testo (es. text-amber-400). */
export function Ciambella({ fette, dimensione = 88, centro }: {
  fette: ReadonlyArray<{ valore: number; colore: string }>;
  dimensione?: number;
  centro?: ReactNode;
}) {
  const totale = fette.reduce((s, f) => s + f.valore, 0) || 1;
  const r = 24;
  const circonferenza = 2 * Math.PI * r;
  let giaPercorso = 0;
  return (
    <div className="relative flex-shrink-0" style={{ width: dimensione, height: dimensione }}>
      <svg viewBox="0 0 64 64" className="h-full w-full -rotate-90" aria-hidden>
        {fette.map((f, i) => {
          const lunghezza = (f.valore / totale) * circonferenza;
          const arco = (
            <circle key={i} cx="32" cy="32" r={r} fill="none" strokeWidth="11" className={cn('stroke-current', f.colore)}
              strokeDasharray={`${Math.max(lunghezza - 1.5, 0.5)} ${circonferenza}`} strokeDashoffset={-giaPercorso} />
          );
          giaPercorso += lunghezza;
          return arco;
        })}
      </svg>
      {centro && <span className="absolute inset-0 flex flex-col items-center justify-center text-center">{centro}</span>}
    </div>
  );
}
